// Extend the Window interface to include the dataLayer property
declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

/**
 * Pushes a data object to the GTM dataLayer.
 *
 * @param data - The object to push into the dataLayer
 */
export const pushToDataLayer = (data: Record<string, any>): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }
};

/**
 * Sends the 'sign_up' event to the dataLayer (SUCCESS only).
 * Ne pas appeler ailleurs que juste après une inscription réussie.
 */
export const trackSignUp = (): void => {
  // dédup simple par session
  if (sessionStorage.getItem('signup_tracked') === '1') return;

  pushToDataLayer({
    event: 'sign_up',
    method: 'email',   // on garde la valeur fixe comme tu veux
    status: 'success', // facultatif, mais utile côté GA4
  });

  sessionStorage.setItem('signup_tracked', '1');
};

/**
 * Sends the 'login' event to the dataLayer with the given method.
 *
 * @param method - e.g. 'email', 'google', etc.
 */
export const trackLogin = (method: string): void => {
  pushToDataLayer({
    event: 'login',
    method,
  });
};

export interface CartItem {
  price?: number;
  quantity: number;
  item_id: string;
  item_name: string;
  coupon?: string;
  discount?: number;
  affiliation?: string;
}

interface PurchaseEventParams {
  transaction_id: string;
  affiliation?: string;
  coupon?: string;
  value: number;
  currency: string;
  items: CartItem[];
}

export const trackPurchase = (params: PurchaseEventParams): void => {
  pushToDataLayer({
    event: 'purchase',
    ecommerce: {
      transaction_id: params.transaction_id,
      value: params.value,
      currency: params.currency,
      items: params.items,
    },
  });
};

export interface UserDataParams {
  email_address: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  street?: string;
  city?: string;
  zip_code?: string;
  country?: string;
}

/**
 * Pushes user identity data to the dataLayer for GA4 user tracking.
 * This is not an event, but rather user attributes (for enhanced measurement).
 */
export const pushUserData = (data: UserDataParams): void => {
  pushToDataLayer({
    user_data: {
      email_address: data.email_address,
      phone_number: data.phone_number,
      address: [
        {
          first_name: data.first_name,
          last_name: data.last_name,
          street: data.street,
          city: data.city,
          zip_code: data.zip_code,
          country: data.country,
        },
      ],
    },
  });
};

export interface CartItem {
  price?: number;
  quantity: number;
  item_id: string;
  item_name: string;
  coupon?: string;
  discount?: number;
  affiliation?: string;
  // If you have other GA4 dimensions (brand, category…), add them here
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
}

interface AddToCartEventParams {
  value: number;
  currency: string;
  items: CartItem[];
}

/**
 * Sends an 'add_to_cart' event to the dataLayer in GA4 format.
 */
export const trackAddToCart = (params: AddToCartEventParams): void => {
  const { value, currency, items } = params;

  // Rebuild the GA4‐expected items array:
  const dlItems = items.map((item) => ({
    affiliation: item.affiliation,
    coupon: item.coupon,
    discount: item.discount,
    price: item.price,
    quantity: item.quantity,
    item_id: item.item_id,
    item_name: item.item_name,
    item_brand: item.item_brand || '',
    item_category: item.item_category || '',
    item_category2: item.item_category2 || '',
    item_category3: item.item_category3 || '',
    item_category4: item.item_category4 || '',
    item_category5: item.item_category5 || '',
    item_list_id: item.item_list_id || '',
    item_list_name: item.item_list_name || '',
    item_variant: item.item_variant || '',
  }));

  pushToDataLayer({
    event: 'add_to_cart',
    ecommerce: {
      value,
      currency,
      items: dlItems,
    },
  });
};

/**
 * Sends the 'start_free_trial' event to the dataLayer with amount and account type.
 *
 * @param amount      - e.g. "10 000 USD"
 * @param accountType - e.g. "STP" or "ECN"
 */
export const trackStartFreeTrial = (
  amount: string,
  accountType: string
): void => {
  pushToDataLayer({
    event: 'start_free_trial',
    trial_account_amount: amount,
    trial_account_type: accountType,
  });
};
